import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Upload, BookOpen, Search } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { ALLERGENS, RECIPE_CATEGORIES, RECIPE_STATUS, fmtMoney } from "@/lib/format";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common/States";
import { AllergenList, RecipeStatusBadge, FoodCostBadge } from "@/components/common/Badges";
import { Can } from "@/components/common/Can";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImportDialog } from "@/components/recipes/ImportDialog";
import { toast } from "sonner";

const ALL = "__all__";

export default function RecipesPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data, loading, error, reload } = useFetch(() => api.get("/recipes").then((r) => r.data), []);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(ALL);
  const [status, setStatus] = useState(params.get("stato") || ALL);
  const [highFc, setHighFc] = useState(false);
  const [without, setWithout] = useState(ALL);
  const [importOpen, setImportOpen] = useState(false);

  const filtered = useMemo(() => (data || []).filter((r) => {
    const ql = q.trim().toLowerCase();
    if (ql && !r.name.toLowerCase().includes(ql) && !(r.ingredients || []).some((i) => (i.name || "").toLowerCase().includes(ql))) return false;
    if (cat !== ALL && r.category !== cat) return false;
    if (status !== ALL && r.status !== status) return false;
    if (highFc && !(r.food_cost_pct > 30)) return false;
    if (without !== ALL && r.allergens?.[without]) return false;
    return true;
  }), [data, q, cat, status, highFc, without]);

  const createNew = async () => {
    try {
      const { data: r } = await api.post("/recipes", { name: "Nuova ricetta", category: "Primi", portions: 4, price: 0, vat_rate: 10, ingredients: [], steps: [] });
      navigate(`/ricette/${r.id}?edit=1`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up" data-testid="recipes-page">
      <PageHeader title="Ricettario" subtitle={data ? `${data.length} ricette · ${data.filter((r) => r.status === "approvata").length} approvate` : ""}
        actions={<>
          <Can perm="recipes.write" variant="outline" onClick={() => setImportOpen(true)} data-testid="import-recipes-button"><Upload className="h-4 w-4" /> Importa da Excel</Can>
          <Can perm="recipes.write" onClick={createNew} data-testid="new-recipe-button"><Plus className="h-4 w-4" /> Nuova ricetta</Can>
        </>} />

      <div className="mb-4 flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center" data-testid="recipe-filters">
        <div className="relative md:w-72"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input placeholder="Cerca per nome o ingrediente" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 bg-white pl-8" data-testid="recipe-search-input" /></div>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="h-9 bg-white md:w-40" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutte le categorie</SelectItem>{RECIPE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-9 bg-white md:w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti gli stati</SelectItem>{Object.entries(RECIPE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        <Select value={without} onValueChange={setWithout}><SelectTrigger className="h-9 bg-white md:w-52" data-testid="filter-without-allergen"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Senza allergene…</SelectItem>{ALLERGENS.map(([c, n]) => <SelectItem key={c} value={c}>Senza {c} · {n}</SelectItem>)}</SelectContent></Select>
        <label className="flex h-9 cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm"><input type="checkbox" checked={highFc} onChange={(e) => setHighFc(e.target.checked)} className="accent-[#1F5F57]" data-testid="filter-high-food-cost" /> Food cost &gt; 30 %</label>
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={reload} /> : filtered.length === 0 ? (
        <EmptyState icon={BookOpen} title={data.length ? "Nessuna ricetta corrisponde ai filtri" : "Il ricettario è vuoto"}
          description={data.length ? "Prova a cambiare ricerca o filtri." : "Crea la prima ricetta: allergeni, costo e food cost verranno calcolati automaticamente dagli ingredienti."} />
      ) : (
        <div className="card overflow-x-auto">
          <Table className="dense-table min-w-[860px]">
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Categoria</TableHead><TableHead>Allergeni</TableHead><TableHead className="text-right">Costo porz.</TableHead><TableHead className="text-right">Prezzo</TableHead><TableHead className="text-right">Food cost</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/ricette/${r.id}`)} data-testid={`recipe-row-${r.id}`}>
                  <TableCell><Link to={`/ricette/${r.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{r.name}</Link>{!r.in_menu && r.category !== "Basi" && <span className="badge badge-grey ml-2">Fuori menu</span>}</TableCell>
                  <TableCell className="text-slate-600">{r.category}</TableCell>
                  <TableCell><AllergenList allergens={r.allergens} /></TableCell>
                  <TableCell className="tnum text-right">{fmtMoney(r.cost_portion)}</TableCell>
                  <TableCell className="tnum text-right">{r.category === "Basi" ? "—" : fmtMoney(r.price)}</TableCell>
                  <TableCell className="text-right">{r.category === "Basi" ? "—" : <FoodCostBadge value={r.food_cost_pct} />}</TableCell>
                  <TableCell><RecipeStatusBadge value={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => reload(true)} />
    </div>
  );
}
